'use client';

import { Box, HStack, Input, Popover, Portal, Text, type InputProps } from '@chakra-ui/react';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { forwardRef, useRef, type ReactNode } from 'react';
import { useAppRailOverlay } from '@/components/core/app-layout/AppRailContext';
import { FILTER_CONTROL_STYLE, FilterPill } from './ui/FilterPill';

export { UnifiedFilterBar as FilterBar } from './InlineFilterControls';

export function FilterPopover({
  children,
  activeCount = 0,
  iconOnly = false,
  ariaLabel = 'Filters',
}: {
  children: ReactNode;
  activeCount?: number;
  iconOnly?: boolean;
  ariaLabel?: string;
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const onRailOverlayChange = useAppRailOverlay();
  return (
    <Popover.Root
      lazyMount
      unmountOnExit
      onInteractOutside={event => {
        // Portalled filter popups (including their search inputs) still belong
        // to this panel, even when a selection has just closed the child menu.
        const target = event.detail.target;
        const owner =
          target instanceof Element
            ? target.closest<HTMLElement>('[data-filter-popup]')?.dataset.filterPopup
            : undefined;
        const controls = contentRef.current?.querySelectorAll<HTMLElement>('[data-filter-control]') ?? [];
        if (owner && Array.from(controls).some(control => control.dataset.filterControl === owner)) {
          event.preventDefault();
        }
      }}
      onOpenChange={({ open }) =>
        onRailOverlayChange({
          open:
            open && Boolean(triggerRef.current?.closest('[data-section="railSectionControls"]')),
        })
      }
      positioning={{
        placement: 'bottom-start',
        strategy: 'fixed',
        gutter: 8,
        flip: true,
        slide: true,
        fitViewport: true,
        overflowPadding: 12,
      }}
    >
      <Popover.Trigger asChild>
        <FilterPill
          ref={triggerRef}
          selected={activeCount > 0}
          gap="1.5"
          px={iconOnly ? '2' : '2.5'}
          aria-label={activeCount ? `${ariaLabel}: ${activeCount} active` : ariaLabel}
          title={ariaLabel}
        >
          <SlidersHorizontal size={14} />
          {iconOnly ? null : 'Filters'}
          {activeCount && !iconOnly ? (
            <Text as="span" fontSize="xs">
              {activeCount}
            </Text>
          ) : null}
        </FilterPill>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner zIndex="popover">
          <Popover.Content
            ref={contentRef}
            layerStyle="surfacePopover"
            w="340px"
            maxW="calc(100vw - 24px)"
            borderRadius="12px"
            p="3"
            maxH="calc(100vh - 48px)"
            overflowY="auto"
          >
            <Popover.Title fontSize="sm" fontWeight="600" mb="3">
              {ariaLabel}
            </Popover.Title>
            {children}
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}

export function FilterClearButton({ onClick }: { onClick: () => void }) {
  return (
    <FilterPill
      px="2"
      color="ui.text.tertiary"
      onClick={onClick}
      aria-label="Clear filters"
      title="Clear filters"
    >
      <X size={14} />
    </FilterPill>
  );
}

export const FilterSearchInput = forwardRef<HTMLInputElement, InputProps>(
  function FilterSearchInput(props, ref) {
    return (
      <HStack
        gap="2"
        h="36px"
        px="2.5"
        w={{ base: '180px', md: '240px' }}
        minW="140px"
        flexShrink={0}
        borderWidth="1px"
        borderColor="ui.border.subtle"
        borderRadius="10px"
      >
        <Box color="ui.text.tertiary" flexShrink={0}>
          <Search size={14} />
        </Box>
        <Input
          ref={ref}
          lineHeight={FILTER_CONTROL_STYLE.lineHeight}
          variant="subtle"
          h="32px"
          px="0"
          minW={0}
          bg="transparent"
          color="ui.text.primary"
          fontSize="0.8125rem"
          _placeholder={{ color: 'ui.text.tertiary' }}
          {...props}
        />
      </HStack>
    );
  }
);

export function FilterDateInput(props: InputProps) {
  return (
    <Input
      {...FILTER_CONTROL_STYLE}
      type="date"
      h="36px"
      w="145px"
      flexShrink={0}
      px="2.5"
      borderRadius="10px"
      borderColor="ui.border.subtle"
      fontSize="0.8125rem"
      color="ui.text.primary"
      {...props}
    />
  );
}
